interface MetricDependencyRecord {
  dependency_metric_ids?: string[] | null
  id?: string
  metric_display_name?: string
  metric_name: string
}

interface MissingMetricDependency {
  dependentMetricName: string
  missingMetricIds: string[]
}

/** Finds dependencies absent from a run without changing its metric selection. */
export function getMissingMetricDependencies(
  selectedMetrics: string[],
  metricRecords: Record<string, MetricDependencyRecord>,
): MissingMetricDependency[] {
  const records = Object.values(metricRecords)
  const resolveMetric = (selector: string) =>
    metricRecords[selector] ??
    records.find(
      (metric) => metric.id === selector || metric.metric_name === selector,
    )
  const selectedIds = new Set(
    selectedMetrics.flatMap((selector) => {
      const id = resolveMetric(selector)?.id
      return id ? [id] : []
    }),
  )
  const recordsById = new Map(
    records.flatMap((metric) => (metric.id ? [[metric.id, metric]] : [])),
  )

  return selectedMetrics.flatMap((selector) => {
    const metric = resolveMetric(selector)
    const missingMetricIds: string[] = []
    const visitedIds = new Set<string>()
    const visitDependencies = (
      candidate: MetricDependencyRecord | undefined,
    ) => {
      for (const dependencyId of candidate?.dependency_metric_ids ?? []) {
        if (visitedIds.has(dependencyId)) {
          continue
        }
        visitedIds.add(dependencyId)
        if (!selectedIds.has(dependencyId)) {
          missingMetricIds.push(dependencyId)
        }
        visitDependencies(recordsById.get(dependencyId))
      }
    }
    visitDependencies(metric)
    return missingMetricIds.length > 0
      ? [
          {
            dependentMetricName: metric?.metric_name ?? selector,
            missingMetricIds,
          },
        ]
      : []
  })
}

export function getMetricDependencyLabel(
  metricId: string,
  metricRecords: Record<string, MetricDependencyRecord>,
): string {
  const metric = Object.values(metricRecords).find(
    (candidate) => candidate.id === metricId,
  )
  return metric?.metric_display_name || metric?.metric_name || metricId
}
